import { TutorialConfig } from "../types/tutorial";

export const overviewPageTutorial: TutorialConfig = {
  id: "overview_page_tour",
  name: "Overview Page Tour",
  description: "Learn about your dashboard and key features",
  steps: [
    {
      element: '[data-tutorial="agenda-section"]',
      popover: {
        title: "📅 Your Agenda",
        description: "Here you'll see your current and upcoming calendar events. Perfect for staying on top of your schedule!",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="chats-section"]',
      popover: {
        title: "💬 Agent Messages",
        description: "Your AI agents will share updates and insights here. Check back regularly for helpful information!",
        side: "left",
      },
    },
    {
      element: '[data-tutorial="priorities-section"]',
      popover: {
        title: "🎯 Your Priorities",
        description: "Keep track of your most important goals and priorities. This helps you stay focused on what matters most.",
        side: "top",
      },
    },
    {
      element: '[data-tutorial="todos-section"]',
      popover: {
        title: "✅ Top Todos",
        description: "Your most important tasks are shown here. Complete them to stay productive and organized!",
        side: "top",
      },
    },
    {
      element: '[data-tutorial="help-icon"]',
      popover: {
        title: "❓ Need Help?",
        description: "Click this help icon anytime to restart this tutorial or get assistance with any page.",
        side: "bottom",
      },
    },
  ],
};

export const chatPageTutorial: TutorialConfig = {
  id: "chat_page_tour",
  name: "Chat Page Tour",
  description: "Learn how to interact with your AI agents and their powerful actions",
  steps: [
    {
      element: '[data-tutorial="agent-list"]',
      popover: {
        title: "🤖 Your AI Agents",
        description: "Choose from different AI agents, each specialized to help with specific tasks and goals. Each agent has unique capabilities and can perform actions on your behalf!",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="chat-input"]',
      popover: {
        title: "💭 Chat Input",
        description:
          "Type your questions or requests here. Your AI agent will respond with helpful insights and can perform actions like creating calendar events, managing todos, or accessing your Google services!",
        side: "top",
      },
    },
    {
      element: '[data-tutorial="chat-history"]',
      popover: {
        title: "📝 Conversation History",
        description:
          "All your conversations are saved here. You can review past discussions, see what actions were performed, and continue where you left off. Look for action buttons that agents can execute!",
        side: "left",
      },
    },
    {
      element: "body",
      popover: {
        title: "⚠️ Important Notice",
        description: "Please read the disclaimer at the bottom of the page, this is a beta product using AI to automate actions. Please verify details before approving actions from chat responses.",
        side: "top",
      },
    },
  ],
};

export const calendarPageTutorial: TutorialConfig = {
  id: "calendar_page_tour",
  name: "Calendar Page Tour",
  description: "Learn how to manage your schedule and events",
  steps: [
    {
      element: '[data-tutorial="calendar-view"]',
      popover: {
        title: "📅 Calendar View",
        description: "Your calendar events are displayed here. You can view, create, and manage all your appointments.",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="event-actions"]',
      popover: {
        title: "⚡ Quick Actions",
        description: "Create new events, sync with Google Calendar, or manage your schedule with these quick action buttons.",
        side: "top",
      },
    },
  ],
};

export const todoPageTutorial: TutorialConfig = {
  id: "todo_page_tour",
  name: "Todo Page Tour",
  description: "Learn how to manage your tasks and stay organized",
  steps: [
    {
      element: '[data-tutorial="todo-list"]',
      popover: {
        title: "📋 Your Tasks",
        description: "All your tasks are organized here. You can add, complete, and prioritize your to-dos.",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="add-todo"]',
      popover: {
        title: "➕ Add New Task",
        description: "Click here to add a new task. Set priorities and deadlines to stay organized!",
        side: "top",
      },
    },
  ],
};

export const prioritiesPageTutorial: TutorialConfig = {
  id: "priorities_page_tour",
  name: "Priorities Page Tour",
  description: "Learn how to set and manage your priorities",
  steps: [
    {
      element: '[data-tutorial="priorities-list"]',
      popover: {
        title: "🎯 Your Priorities",
        description: "Set and track your most important goals and priorities here. This helps you focus on what matters most.",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="add-priority"]',
      popover: {
        title: "➕ Add Priority",
        description: "Create new priorities and goals to help guide your daily actions and decisions.",
        side: "top",
      },
    },
  ],
};

export const coachesPageTutorial: TutorialConfig = {
  id: "coaches_page_tour",
  name: "Coaches Page Tour",
  description: "Learn how to work with your AI coaches",
  steps: [
    {
      element: '[data-tutorial="coaches-list"]',
      popover: {
        title: "🏃‍♂️ Your Coaches",
        description:
          "Your AI coaches are here to help you achieve your goals. Each coach specializes in different areas like sales, marketing, operations, and more. Click on any coach to start a coaching session!",
        side: "right",
      },
    },
  ],
};

export const profilePageTutorial: TutorialConfig = {
  id: "profile_page_tour",
  name: "Profile Page Tour",
  description: "Learn how to manage your profile and settings",
  steps: [
    {
      element: '[data-tutorial="profile-info"]',
      popover: {
        title: "👤 Your Profile",
        description: "View and edit your personal information, company details, and job role.",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="profile-settings"]',
      popover: {
        title: "⚙️ Settings",
        description: "Manage your account settings, preferences, and privacy options.",
        side: "top",
      },
    },
  ],
};

export const sidebarTutorial: TutorialConfig = {
  id: "sidebar_navigation_tour",
  name: "Navigation Sidebar Tour",
  description: "Learn how to navigate between different sections of the app",
  steps: [
    {
      element: '[data-tutorial="overview-nav"]',
      popover: {
        title: "📊 Overview",
        description: "Your dashboard where you can see your agenda, recent chats, priorities, and todos all in one place. Perfect for getting a quick overview of your day!",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="chat-nav"]',
      popover: {
        title: "💬 Chat",
        description: "Chat with your AI agents to get help with tasks, ask questions, and receive personalized assistance. Each agent has different specialties and can perform actions for you!",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="calendar-nav"]',
      popover: {
        title: "📅 Calendar",
        description: "Manage your schedule, create events, and sync with Google Calendar. View your calendar in different formats and drag events to reschedule them!",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="coaches-nav"]',
      popover: {
        title: "🏃‍♂️ Coaches",
        description: "Work with AI coaches to improve your business performance! Each coach specializes in different areas like sales, marketing, and operations.",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="priorities-nav"]',
      popover: {
        title: "🎯 Priorities",
        description: "Set and manage your most important goals and priorities. Organize them by category and stay focused on what matters most!",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="todo-nav"]',
      popover: {
        title: "✅ Todo List",
        description: "Keep track of your tasks and stay organized! Create todos, set priorities, add deadlines, and track your progress.",
        side: "right",
      },
    },
    {
      element: '[data-tutorial="profile-nav"]',
      popover: {
        title: "👤 Profile",
        description: "Manage your personal information, company details, and account settings. Keep your profile up to date for the best experience!",
        side: "right",
      },
    },
  ],
};

// Export all tutorials for easy access
export const tutorials = {
  sidebar: sidebarTutorial,
  overview: overviewPageTutorial,
  chat: chatPageTutorial,
  calendar: calendarPageTutorial,
  todo: todoPageTutorial,
  priorities: prioritiesPageTutorial,
  coaches: coachesPageTutorial,
  profile: profilePageTutorial,
};
